// The sign-in page for the protected NorthStar pages (/login).
//
// Accounts are Netlify Identity. The gate (netlify/edge-functions/editor-auth.js)
// sends anybody without a session here as /login?next=<the page they wanted>,
// and checks the session this page leaves behind: the nf_jwt and nf_refresh
// cookies that @netlify/identity writes. So everything here ends one of two
// ways: those cookies are set and the visitor goes back to `next`, or they are
// told what to do instead.
//
// Netlify's own emails link to the front page with a token in the hash
// (#invite_token=…, #recovery_token=…, #confirmation_token=…); src/index.html
// forwards those here, and handleAuthCallback() reads them.

import {
  login, logout, signup, getUser, getSettings, handleAuthCallback,
  acceptInvite, requestPasswordRecovery, updateUser,
} from "@netlify/identity";

const app = document.getElementById("app");
const params = new URLSearchParams(location.search);
// Only a path on this site may be returned to: never another origin, never a
// scheme-relative "//host" or a backslash some browsers read as one.
const safeNext = (raw) => (typeof raw === "string" && raw.length <= 512 && /^\/(?![/\\])[^\s\\]*$/.test(raw) ? raw : "/");
const next = safeNext(params.get("next"));
const PW_MIN = 8;

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

// @netlify/identity writes its cookies for the browser session only, so every
// browser restart would mean signing in again. The gate keeps them for 30 days
// when it renews them; this does the same after a sign-in here.
const MAX_AGE = 60 * 60 * 24 * 30;
function persist() {
  for (const name of ["nf_jwt", "nf_refresh"]) {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    if (m) document.cookie = `${name}=${m[1]}; path=/; secure; samesite=lax; max-age=${MAX_AGE}`;
  }
}
function proceed() {
  persist();
  location.replace(next);
}

// What GoTrue says is written for developers; these are the ones people meet.
function friendly(err, fallback) {
  const msg = String(err?.message || "");
  if (/invalid|no user found|password/i.test(msg) && /login|grant|password|user/i.test(msg)) return "That email and password don't match an account.";
  if (/not confirmed/i.test(msg)) return "Confirm your email address first: open the link in the email you were sent.";
  if (/rate|too many/i.test(msg)) return "Too many attempts. Wait a minute and try again.";
  if (/network|fetch|timeout|failed to fetch/i.test(msg)) return "Couldn't reach the sign-in service. Check your connection and try again.";
  return fallback || msg || "Something went wrong. Please try again.";
}

let signupOpen = false;

function view({ sub, note = "", noteKind = "ok", fields = "", button = "", after = "" }) {
  app.innerHTML = `<div class="brand">◆ NorthStar protected zone</div>
    <p class="sub">${sub}</p>
    ${note ? `<div class="${noteKind}" role="${noteKind === "err" ? "alert" : "status"}">${esc(note)}</div>` : ""}
    ${fields || button ? `<form id="f" novalidate>${fields}${button ? `<button class="go" type="submit">${button}</button>` : ""}</form>` : ""}
    ${after}`;
  const first = app.querySelector("input:not([type=hidden])");
  if (first) first.focus();
  return app.querySelector("#f");
}

// Runs a form's action with the button disabled; an error re-renders the view
// with the message and whatever the visitor had typed (never a password).
function onSubmit(form, action) {
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const btn = form.querySelector("button[type=submit]");
    const data = Object.fromEntries(new FormData(form));
    btn.disabled = true;
    try {
      await action(data);
    } finally {
      if (btn.isConnected) btn.disabled = false;
    }
  });
}

function passwordProblem(password, confirm) {
  if (password.length < PW_MIN) return `Choose a password of at least ${PW_MIN} characters.`;
  if (password !== confirm) return "The two passwords do not match.";
  return "";
}

const emailField = (value = "", autocomplete = "username") =>
  `<input class="pw" type="email" name="email" placeholder="Email" value="${esc(value)}" autocomplete="${autocomplete}" required>`;
const newPasswordFields = `<input class="pw" type="password" name="password" placeholder="New password" autocomplete="new-password" minlength="${PW_MIN}" required>
      <input class="pw" type="password" name="confirm" placeholder="Repeat password" autocomplete="new-password" minlength="${PW_MIN}" required>
      <p class="hint">At least ${PW_MIN} characters.</p>`;

function link(id, text) { return `<button type="button" id="${id}">${text}</button>`; }
function wire(id, fn) { const el = app.querySelector(`#${id}`); if (el) el.addEventListener("click", fn); }

// ---- the views -------------------------------------------------------------------

function showSignIn({ note = "", noteKind = "ok", email = "" } = {}) {
  const form = view({
    sub: "Sign in with the email address and password of your account.",
    note, noteKind,
    fields: `${emailField(email)}
      <input class="pw" type="password" name="password" placeholder="Password" autocomplete="current-password" required>`,
    button: "Sign in",
    after: `<p class="links">${link("forgot", "Forgot your password?")}${signupOpen ? `<br>No account? ${link("signup", "Create one")}` : "<br>No account? Ask the site admin for an invitation."}</p>`,
  });
  wire("forgot", () => showForgot());
  wire("signup", () => showSignUp());
  onSubmit(form, async ({ email: e, password }) => {
    if (!e || !password) return showSignIn({ note: "Enter your email address and password.", noteKind: "err", email: e });
    try {
      await login(e.trim(), password);
      proceed();
    } catch (err) {
      showSignIn({ note: friendly(err), noteKind: "err", email: e });
    }
  });
}

function showForgot({ note = "", noteKind = "ok", email = "" } = {}) {
  const form = view({
    sub: "Enter the email address of your account and we'll send you a link to choose a new password.",
    note, noteKind,
    fields: emailField(email, "email"),
    button: "Send the link",
    after: `<p class="links">${link("back", "Back to sign in")}</p>`,
  });
  wire("back", () => showSignIn({ email }));
  onSubmit(form, async ({ email: e }) => {
    if (!e) return showForgot({ note: "Enter your email address.", noteKind: "err" });
    try {
      await requestPasswordRecovery(e.trim());
      // Said whether or not the address has an account, so the form does not
      // tell a stranger which addresses do.
      showSignIn({ note: "If that address has an account, an email with a link to choose a new password is on its way.", email: e });
    } catch (err) {
      showForgot({ note: friendly(err, "Couldn't send the email. Please try again."), noteKind: "err", email: e });
    }
  });
}

function showInvite(token, { note = "", noteKind = "err" } = {}) {
  const form = view({
    sub: "You've been invited. Choose the password you will sign in with.",
    note, noteKind,
    fields: newPasswordFields,
    button: "Create my account",
  });
  onSubmit(form, async ({ password, confirm }) => {
    const bad = passwordProblem(password, confirm);
    if (bad) return showInvite(token, { note: bad });
    let user;
    try {
      user = await acceptInvite(token, password);
    } catch (err) {
      return showSignIn({
        note: /not found|invalid|expired/i.test(String(err?.message))
          ? "This invitation link has expired or was already used. If you set a password with it, sign in below; otherwise ask the site admin for a new invitation."
          : friendly(err),
        noteKind: "err",
      });
    }
    // acceptInvite() starts a session in the browser but writes no cookies, and
    // the cookies are what the gate checks. Signing in with the password just
    // chosen is the documented way to write them.
    try {
      await login(user.email, password);
      proceed();
    } catch {
      showSignIn({ note: "Your account is ready. Sign in with your new password.", email: user.email });
    }
  });
}

function showNewPassword({ note = "", noteKind = "err" } = {}) {
  const form = view({
    sub: "Choose a new password for your account.",
    note, noteKind,
    fields: newPasswordFields,
    button: "Save the new password",
  });
  onSubmit(form, async ({ password, confirm }) => {
    const bad = passwordProblem(password, confirm);
    if (bad) return showNewPassword({ note: bad });
    try {
      await updateUser({ password });
      proceed();
    } catch (err) {
      showNewPassword({ note: friendly(err, "Couldn't save the password. Please try again.") });
    }
  });
}

function showSignUp({ note = "", noteKind = "err", email = "" } = {}) {
  const form = view({
    sub: "Create an account with your email address. We'll send you a link to confirm it.",
    note, noteKind,
    fields: `${emailField(email, "email")}${newPasswordFields}`,
    button: "Create account",
    after: `<p class="links">Already have an account? ${link("back", "Sign in")}</p>`,
  });
  wire("back", () => showSignIn({ email }));
  onSubmit(form, async ({ email: e, password, confirm }) => {
    if (!e) return showSignUp({ note: "Enter your email address." });
    const bad = passwordProblem(password, confirm);
    if (bad) return showSignUp({ note: bad, email: e });
    try {
      const user = await signup(e.trim(), password);
      if (user.confirmedAt) return proceed();
      showSignIn({ note: "Almost there: open the link in the email we just sent to confirm your address, then sign in.", email: e });
    } catch (err) {
      showSignUp({ note: friendly(err), email: e });
    }
  });
}

function showSignedIn(user, { note = "" } = {}) {
  view({
    sub: `Signed in as <span class="who">${esc(user.email)}</span>.`,
    note,
    after: `<button class="go" type="button" id="continue">Continue</button>
      <button class="go alt" type="button" id="signout">Sign out</button>`,
  });
  wire("continue", proceed);
  wire("signout", async () => {
    try { await logout(); } catch { /* the cookies go either way */ }
    showSignIn({ note: "You are signed out." });
  });
}

// ---- start -------------------------------------------------------------------------

async function start() {
  try {
    signupOpen = !(await getSettings()).disableSignup;
  } catch {
    signupOpen = false;
  }

  let result = null;
  try {
    result = await handleAuthCallback();
  } catch (err) {
    history.replaceState(null, "", location.pathname + location.search);
    return showSignIn({ note: "That link has expired or was already used. Sign in, or ask for a new one.", noteKind: "err" });
  }
  if (result?.type === "invite" && result.token) return showInvite(result.token);
  if (result?.type === "recovery") return showNewPassword({ note: "", noteKind: "ok" });
  if (result?.type === "confirmation" || result?.type === "oauth") return proceed();

  // Arrived from ?signout=1: the gate has cleared the cookies; this ends the
  // browser's own copy of the session as well.
  if (params.has("signedout")) {
    try { await logout(); } catch { /* nothing left to end */ }
    return showSignIn({ note: "You are signed out." });
  }

  const user = await getUser();
  if (user) return showSignedIn(user, { note: result?.type === "email_change" ? "Your email address has been changed." : "" });
  showSignIn();
}

start();
